/**
 * @Page WST02
 * @Description [WST02] 실사재고 - 현황관리 - 재고현황
 * @Table WRTWQTY 실재고 수량
 * 기준일자 ±7일 날짜 컬럼 + 기준일자 빨간색 하이라이트
 * 수량 클릭 시 해당 날짜/품번의 차수별 상세 모달 표출
 **/
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Modal, Box } from "@material-ui/core";
import Ribbon from '@/components/organisms/Ribbon';
import GridRibbon from "@/components/organisms/GridRibbon";
import ContentXearch from '@/components/organisms/ContentXearch';
import Content from '@/components/organisms/Content';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import * as Common from '@/utils/Common';
import CommonUtil from '@/utils/CommonUtil';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const Utils = new CommonUtil();

// ─── 날짜 유틸 ────────────────────────────────────────────────────────────────
/** YYYY-MM-DD → Date */
function parseDate(str) {
    if (!str) return new Date();
    const s = str.replace(/-/g, "");
    return new Date(
        parseInt(s.substring(0, 4)),
        parseInt(s.substring(4, 6)) - 1,
        parseInt(s.substring(6, 8))
    );
}

/** Date → YYYY-MM-DD */
function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/** Date → YYYYMMDD */
function formatDateNoHyphen(d) {
    return formatDate(d).replace(/-/g, "");
}

// ─── WareOrdr 팝업과 동일한 MUI Modal 스타일 ───────────────────────────────
const ordrModalBoxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    height: 560,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    paddingTop: '10px',
    paddingBottom: '10px',
};

// ─── 차수별 상세 팝업 (RST01 WareOrdr 팝업과 동일한 구조) ────────────────────
const OrdrDetailModal = ({ open, onClose, ptno, stdt, detailList }) => {

    const displayPtno = ptno && ptno.length > 5
        ? ptno.substring(0, 5) + "-" + ptno.substring(5)
        : ptno;
    const displayStdt = stdt
        ? stdt.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")
        : "";

    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "13px",
        fontFamily: "PretendardLight, NanumGothic, sans-serif",
    };
    const thStyle = {
        background: "#f0f4fa",
        border: "1px solid #d0d7e5",
        padding: "7px 10px",
        textAlign: "center",
        fontWeight: "bold",
        color: "#334",
        fontSize: "13px",
    };
    const tdStyle = {
        border: "1px solid #e0e5ef",
        padding: "7px 10px",
        textAlign: "center",
        color: "#444",
        fontSize: "13px",
    };
    const tdRightStyle = { ...tdStyle, textAlign: "right" };
    const tdLeftStyle  = { ...tdStyle, textAlign: "left" };

    return (
        <Modal
            open={open}
            onClose={onClose}
        >
            <Box sx={ordrModalBoxStyle}>
                {/* 닫기 버튼 - WareOrdr 팝업과 동일 */}
                <button
                    onClick={onClose}
                    style={{
                        border: "none",
                        float: "right",
                        paddingRight: "0px",
                        background: "transparent",
                        cursor: "pointer",
                    }}
                >
                    <img src="/images/button/close_button.png" alt="close" />
                </button>

                {/* Ribbon - WareOrdr 팝업과 동일한 구조 */}
                <Ribbon
                    newTabUnused={true}
                    onInfoManual={false}
                >
                    <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif" }}>
                        차수별 재고 상세&nbsp;|&nbsp;{displayPtno}&nbsp;|&nbsp;{displayStdt}
                    </b>
                </Ribbon>

                {/* 테이블 영역 - WareOrdr의 grid_type01과 동일한 래핑 */}
                <div className="grid_type01" style={{ marginTop: "10px" }}>
                    <div className="grid_wrap01">
                        <div className="grid_cont_box" style={{ height: "380px", width: "100%", overflowY: "auto" }}>
                            {detailList && detailList.length > 0 ? (
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>NO</th>
                                            <th style={thStyle}>차수</th>
                                            <th style={thStyle}>수량</th>
                                            <th style={thStyle}>비고</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailList.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                style={{ background: idx % 2 === 0 ? "#fff" : "#f8faff" }}
                                            >
                                                <td style={tdStyle}>{idx + 1}</td>
                                                <td style={tdStyle}>{row.ordrdisp || row.ordr || ""}</td>
                                                <td style={tdRightStyle}>
                                                    {row.waqt !== undefined && row.waqt !== null
                                                        ? Number(row.waqt).toLocaleString()
                                                        : ""}
                                                </td>
                                                <td style={tdLeftStyle}>{row.etc || ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    height: "100%", color: "#999", fontSize: "13px",
                                    fontFamily: "PretendardLight, NanumGothic, sans-serif",
                                }}>
                                    조회된 데이터가 없습니다.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

// ─── WST02 메인 컴포넌트 ───────────────────────────────────────────────────────
function WST02() {
    const token = Common.getToken();
    const userId = Common.getToken("id");
    const tVnt1 = Common.getToken("vand");
    const tComp = Common.getToken("comp");
    const ckVnt1 = Common.getCookie("ck_vnt1" + userId) ? Common.getCookie("ck_vnt1" + userId) : tVnt1;

    const grdWST02Main = useRef();    // 메인 그리드 (날짜별 재고현황)

    // ─── 상태 ────────────────────────────────────────────────────────────────
    const [idata, setData] = useState({
        kVnt1: ckVnt1,
        kComp: tComp,
        kWrcd: "",          // 창고코드
        kGrtp: "",          // 품번그룹
        kRecd: "",          // 상주원
        kPtno: "",          // 품번
        kStdt: Common.getToday(), // 기준일자

        // 콤보 목록
        wrcdList: [],       // 창고코드 목록
        grptList: [],       // 품번그룹 목록
        recdList: [],       // 상주원 목록
    });

    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const [dlgMessage, setDlgMessage] = useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    let [searchApi, setSearchApi] = useState(false);
    const onClearApi = useCallback(async () => {
        setSearchApi(false);
    }, []);

    const [panded, setPanded] = useState(true);
    const [style, setStyle] = useState({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });

    // ─── 차수 상세 모달 상태 ──────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [modalPtno, setModalPtno] = useState("");
    const [modalStdt, setModalStdt] = useState("");
    const [modalDetailList, setModalDetailList] = useState([]);

    // ─── 최초 렌더 여부 (기준일자 자동조회 중복 방지) ─────────────────────────
    const isFirstRender = useRef(true);

    // ─── Mount: 콤보 데이터 조회 ──────────────────────────────────────────────
    useEffect(() => {
        onSearchWrcdList();
        onSearchGrptList();
        onSearchRecdList();
    }, []);

    // ─── 기준일자 변경 시 자동 재조회 ─────────────────────────────────────────
    // kStdt 가 바뀔 때마다 자동으로 조회 API 호출
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (!idata.kStdt) return;
        const stdt = idata.kStdt.replace(/-/g, "");
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","grtp":"${idata.kGrtp}","recd":"${idata.kRecd}","ptno":"${idata.kPtno}","stdt":"${stdt}"}`;
        const param = encodeURI(para);
        setSearchApi(SERVER_URL + "/wst02/qty/search?query=" + param);
    }, [idata.kStdt]);

    // 창고코드 목록 조회
    const onSearchWrcdList = async () => {
        const para = `{"kVnt1":"${ckVnt1}","kWrcd":"","wareyn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst01/ware/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(w => ({ comCode: w.wrcd, text: `${w.wrcd} - ${w.wrnm}` }))];
                    setData(prev => ({ ...prev, wrcdList: list }));
                }
            })
            .catch(() => {});
    };

    // 품번그룹 목록 조회
    const onSearchGrptList = async () => {
        const para = `{"kVnt1":"${ckVnt1}","kGrtp":"","kGryn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst03/wagr/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(g => ({ comCode: g.grtp, text: `${g.grtp} - ${g.grnm}` }))];
                    setData(prev => ({ ...prev, grptList: list }));
                }
            })
            .catch(() => {});
    };

    // 상주원 목록 조회
    const onSearchRecdList = async () => {
        const para = `{"kVnt1":"${ckVnt1}","recd":"","useyn":"Y"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst02/resi/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(r => ({ comCode: r.recd, text: `${r.recd} - ${r.renm}` }))];
                    setData(prev => ({ ...prev, recdList: list }));
                }
            })
            .catch(() => {});
    };

    // ─── Control 변경 ─────────────────────────────────────────────────────────
    const onChange = async (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    // ─── 조회 ─────────────────────────────────────────────────────────────────
    const onSearch = async () => {
        const stdt = idata.kStdt ? idata.kStdt.replace(/-/g, "") : "";
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","grtp":"${idata.kGrtp}","recd":"${idata.kRecd}","ptno":"${idata.kPtno}","stdt":"${stdt}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/wst02/qty/search?query=" + param;
        setSearchApi(query);
    };

    // ─── 날짜 클릭 시 차수별 상세 조회 (stdt: YYYYMMDD 직접 전달) ────────────
    const onClickQty = async (ptno, stdtNo, wrcd) => {
        if (!ptno || !stdtNo) return;
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${wrcd}","ptno":"${ptno}","stdt":"${stdtNo}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/wst01/qty/search?query=" + param;

        setModalPtno(ptno);
        setModalStdt(stdtNo);
        setModalDetailList([]);
        setModalOpen(true);

        try {
            const resp = await fetch(query, { headers: { Authorization: token } });
            const data = await resp.json();
            if (data.success && data.data) {
                setModalDetailList(data.data);
            }
        } catch (e) {
            setModalDetailList([]);
        }
    };

    // ─── AlertDialog ──────────────────────────────────────────────────────────
    const AlertDialogResult = async (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    };

    // ─── 검색조건 ─────────────────────────────────────────────────────────────
    const optionsX = {
        0: [
            {
                formatter: "input", name: "kStdt", labelName: "기준일자", type: "date",
                defaultValue: idata.kStdt, align: "left", labelType: "none",
            },
            {
                formatter: "select", name: "kWrcd", labelName: "창고코드", type: "text",
                defaultValue: idata.kWrcd, codeMode: false, selectAll: "",
                codes: idata.wrcdList, comms: null,
            },
            {
                formatter: "select", name: "kGrtp", labelName: "품번그룹", type: "text",
                defaultValue: idata.kGrtp, codeMode: false, selectAll: "",
                codes: idata.grptList, comms: null,
            },
            {
                formatter: "select", name: "kRecd", labelName: "상주원", type: "text",
                defaultValue: idata.kRecd, codeMode: false, selectAll: "",
                codes: idata.recdList, comms: null,
            },
            {
                formatter: "input", name: "kPtno", labelName: "품번", type: "text",
                defaultValue: idata.kPtno, readOnly: false, align: "left", labelType: "none",
            },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
        ],
    };

    // ─── 날짜 인덱스 목록 (-7 ~ +7) ─────────────────────────────────────────
    // 백엔드 응답 필드: d_7~d_1, d0~d7 (날짜 YYYYMMDD), q_7~q_1, q0~q7 (수량)
    // 백엔드 DTO 필드명: 과거(음수)는 p접두어 사용 (dp7, qp7 등)
    // 기준일 이후(0 이상)는 기존과 동일 (d0, q0 / d1, q1 등)
    const DATE_KEYS = [
        { dKey: "dp7", qKey: "qp7", offset: -7 },
        { dKey: "dp6", qKey: "qp6", offset: -6 },
        { dKey: "dp5", qKey: "qp5", offset: -5 },
        { dKey: "dp4", qKey: "qp4", offset: -4 },
        { dKey: "dp3", qKey: "qp3", offset: -3 },
        { dKey: "dp2", qKey: "qp2", offset: -2 },
        { dKey: "dp1", qKey: "qp1", offset: -1 },
        { dKey: "d0",  qKey: "q0",  offset:  0 },
        { dKey: "d1",  qKey: "q1",  offset:  1 },
        { dKey: "d2",  qKey: "q2",  offset:  2 },
        { dKey: "d3",  qKey: "q3",  offset:  3 },
        { dKey: "d4",  qKey: "q4",  offset:  4 },
        { dKey: "d5",  qKey: "q5",  offset:  5 },
        { dKey: "d6",  qKey: "q6",  offset:  6 },
        { dKey: "d7",  qKey: "q7",  offset:  7 },
    ];

    // ─── Grid 컬럼 정의 ────────────────────────────────────────────────────────
    const columnDefs = useMemo(() => {
        // 기준일자(YYYYMMDD) - d0 컬럼이 기준일
        const baseStr = idata.kStdt ? idata.kStdt.replace(/-/g, "") : Common.getToday().replace(/-/g, "");

        // 고정 컬럼
        const fixedCols = [
            {
                headerName: "창고코드", field: "wrcd",
                sortable: true, filter: true, flex: 1, minWidth: 60, maxWidth: 80,
                editable: false, cellClass: ["nocheckbox", "alignC"],
                pinned: "left",
            },
            {
                headerName: "품번그룹", field: "grtp",
                sortable: true, filter: true, flex: 1, minWidth: 60, maxWidth: 110,
                editable: false, cellClass: ["nocheckbox", "alignC"],
                pinned: "left",
            },
            {
                headerName: "상주원", field: "renm",
                sortable: true, filter: true, flex: 1, minWidth: 50, maxWidth: 80,
                editable: false, cellClass: ["nocheckbox", "alignC"],
                pinned: "left",
            },
            {
                headerName: "품번", field: "ptno",
                sortable: true, filter: true, flex: 2, minWidth: 120, maxWidth: 160,
                editable: false, cellClass: ["nocheckbox", "alignL"],
                pinned: "left",
                valueGetter: Utils.partnoGetter,
            },
        ];

        // 날짜 컬럼: d_7~d_1, d0~d7 → q_7~q_1, q0~q7
        // 기준일자(idata.kStdt) + offset 으로 헤더 날짜를 직접 계산
        // → kStdt 변경 즉시 useMemo 재실행되어 헤더가 실시간 갱신됨
        const dateCols = DATE_KEYS.map(({ dKey, qKey, offset }) => {
            const isBase = offset === 0;

            // 헤더 라벨: idata.kStdt + offset 으로 직접 MM/DD 계산
            const base = parseDate(idata.kStdt || Common.getToday());
            const targetDay = new Date(base);
            targetDay.setDate(base.getDate() + offset);
            const mm = String(targetDay.getMonth() + 1).padStart(2, "0");
            const dd = String(targetDay.getDate()).padStart(2, "0");
            const headerLabel = `${mm}/${dd}`;

            return {
                headerName: headerLabel,
                field: qKey,
                sortable: true,
                filter: true,
                flex: 1,
                minWidth: 68,
                editable: false,
                cellClass: ["nocheckbox", "alignR"],
                headerClass: isBase ? "wst02-base-date-header" : "",
                cellStyle: (params) => {
                    const val = params.value;
                    const baseStyle = isBase
                        ? { color: "#d32f2f", fontWeight: "bold", background: "#fff5f5" }
                        : {};
                    if (val !== null && val !== undefined && val !== "" && Number(val) !== 0) {
                        return {
                            ...baseStyle,
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlinePosition: "under",
                            color: isBase ? "#d32f2f" : "#1976d2",
                            fontWeight: "bold",
                        };
                    }
                    return isBase ? { ...baseStyle } : null;
                },
                onCellClicked: (params) => {
                    console.log("params>>>>.")
                    console.log(params)
                    
                    const val = params.value;
                    if (val !== null && val !== undefined && val !== "" && Number(val) !== 0) {
                        const rawPtno = params.data?.ptno || "";
                        const dateVal = params.data?.[dKey] || "";  // YYYYMMDD
                        const wrcd = params.data?.wrcd || "";
                        onClickQty(rawPtno, dateVal, wrcd);
                    }
                },
                valueFormatter: (params) => {
                    if (params.value === null || params.value === undefined || params.value === "") return "";
                    return Number(params.value).toLocaleString();
                },
            };
        });

        return [...fixedCols, ...dateCols];
    }, [idata.kStdt]);

    const onClick = async () => {
        setPanded(!panded);
        if (panded === true) {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 8px)" });
        } else {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });
        }
    };

    return (
        <>
            {/* 차수별 상세 모달 */}
            <OrdrDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                ptno={modalPtno}
                stdt={modalStdt}
                detailList={modalDetailList}
            />

            {/* Dialog */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={5000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />

            {/* Ribbon */}
            <Ribbon
                onSearch={onSearch} onSearchName={null}
            >
                {"실사재고 > 현황관리 > "}
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardMedium, NanumGothic, sans-serif" }}>
                    재고현황
                </b>
            </Ribbon>

            {/* 검색조건 */}
            <ContentXearch optionsX={optionsX} onChange={onChange} onClick={onClick} />

            {/* Content */}
            <Content style={style}>
                {/* 기준일자 헤더 스타일 인라인 삽입 */}
                <style>{`
                    .wst02-base-date-header {
                        color: #d32f2f !important;
                        font-weight: bold !important;
                        background: #fff0f0 !important;
                    }
                    .wst02-base-date-header .ag-header-cell-text {
                        color: #d32f2f !important;
                    }
                `}</style>

                <div className="grid_type01">
                    <div className="grid_wrap01">
                        <GridRibbon
                            title={"재고현황 (기준일자 ±7일)"}
                            onSearch={onSearch} onSearchName={null}
                        />
                        <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                            <Grid
                                ref={grdWST02Main}
                                gridId={"WST02"}
                                columnDefs={columnDefs}
                                pagination={true}
                                paginationCsv={true}
                                paginationPageSize={"1000"}
                                rowMultiSelectWithClick={false}
                                suppressRowClickSelection={true}
                                rowSelection={"single"}
                                enableRangeSelection={false}
                                enableCellTextSelection={true}
                                isFirst={false}
                                displayNo={true}
                                searchApi={searchApi}
                                onClearApi={onClearApi}
                                onCellValueChangedEvent={async () => {}}
                            />
                        </div>
                    </div>
                </div>
            </Content>
        </>
    );
}

export default WST02;
